import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Modal, Input, Typography, message } from 'antd';

import { useEditorDoc, useEditorDispatch, useEditorMeta, useEditorUi } from '../../EditorProvider';
import { cellIndex, lineCells, normalizeSolution, type WordsearchDoc } from './doc';
import type { WordsearchAction } from './reducer';
import { newRef, type WordsearchUi } from './ui';
import { Cell } from './Cell';

// A grade. Colocar palavra = arrastar da célula inicial (mousedown) até a final
// (mouseup); clique simples posiciona o CURSOR e o teclado edita as letras de
// recheio (A–Z escreve, Backspace/Delete limpa, setas movem).
//
// A regra de perf do spec vive aqui: quem segura as 225 células é o `memo` da
// `Cell`, que não assina contexto nenhum e só recebe props primitivas. Para o memo
// bater, os handlers passados a ela precisam de referência ESTÁVEL entre renders —
// por isso lêem `doc`/`ui`/`editavel` via `stateRef` (mesmo truque do
// `crossword/Canvas.tsx`) em vez de entrar nas dependências do `useCallback`.
// (O DocContext tem outros assinantes — `Inspector` e `EditorShell` —, mas eles são
// um componente cada; o custo que importava era o das células.)

/** Uma reta da grade, em coordenadas 1-indexadas. */
interface Line {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}

/** O arrasto esperando a confirmação do autor (o modal "que palavra é essa?"). */
interface PendingWord extends Line {
  length: number;
}

const cellsOf = (line: Line) => lineCells({ ref: '', solution: '', clue: '', ...line });

/** TODAS as palavras colocadas que passam por (x,y), com a letra que cada uma exige ali. */
function wordsAtCell(
  doc: WordsearchDoc,
  x: number,
  y: number,
): Array<{ solution: string; expected: string }> {
  const owners: Array<{ solution: string; expected: string }> = [];
  for (const w of doc.words) {
    const i = lineCells(w).findIndex((c) => c.x === x && c.y === y);
    if (i !== -1) owners.push({ solution: w.solution, expected: w.solution[i] ?? '' });
  }
  return owners;
}

/** "SOL", "SOL e GATO" ou "SOL, GATO e MAR" — lista em português, sem vírgula solta antes do "e". */
function joinSolutions(solutions: string[]): string {
  if (solutions.length <= 1) return solutions.join('');
  return `${solutions.slice(0, -1).join(', ')} e ${solutions[solutions.length - 1]}`;
}

/** Duas retas são a MESMA reta (o sentido do arrasto não importa aqui). */
function isSameLine(a: Line, b: Line): boolean {
  return (
    (a.x1 === b.x1 && a.y1 === b.y1 && a.x2 === b.x2 && a.y2 === b.y2) ||
    (a.x1 === b.x2 && a.y1 === b.y2 && a.x2 === b.x1 && a.y2 === b.y1)
  );
}

export const Canvas: React.FC = () => {
  const doc = useEditorDoc<WordsearchDoc>();
  const dispatchApi = useEditorDispatch();
  const [ui, setUi] = useEditorUi<WordsearchUi>();
  const { editavel } = useEditorMeta();
  const [hover, setHover] = useState<{ x: number; y: number } | null>(null);
  const [pending, setPending] = useState<PendingWord | null>(null);
  const [typed, setTyped] = useState('');
  const [pendingError, setPendingError] = useState<string | null>(null);
  const gridRef = useRef<HTMLDivElement | null>(null);

  // `editavel` entra aqui junto com doc/ui: os handlers precisam consultá-lo sem
  // entrar nas dependências do useCallback (referência estável = memo da Cell vale).
  const stateRef = useRef({ doc, ui, editavel });
  stateRef.current = { doc, ui, editavel };

  const dispatch = useCallback(
    (action: WordsearchAction) => dispatchApi.do(action),
    [dispatchApi],
  );

  const dragCells = useMemo(() => {
    if (!ui.dragStart || !hover) return new Set<string>();
    const cells = cellsOf({ x1: ui.dragStart.x, y1: ui.dragStart.y, x2: hover.x, y2: hover.y });
    return new Set(cells.map((c) => `${c.x},${c.y}`));
  }, [ui.dragStart, hover]);

  const wordCells = useMemo(() => {
    const set = new Set<string>();
    for (const w of doc.words) {
      for (const c of lineCells(w)) set.add(`${c.x},${c.y}`);
    }
    return set;
  }, [doc.words]);

  const selectedCells = useMemo(() => {
    const w = doc.words.find((it) => it.ref === ui.selectedRef);
    if (!w) return new Set<string>();
    return new Set(lineCells(w).map((c) => `${c.x},${c.y}`));
  }, [doc.words, ui.selectedRef]);

  const onMouseDown = useCallback(
    (x: number, y: number) => {
      // Jogo com ranking é somente leitura: sem isto o autor arrasta e digita
      // normalmente e só descobre a trava no Salvar (o Inspector já respeitava).
      if (!stateRef.current.editavel) return;
      setUi((prev) => ({ ...prev, dragStart: { x, y } }));
      setHover({ x, y });
    },
    [setUi],
  );

  const onMouseEnter = useCallback((x: number, y: number) => {
    if (stateRef.current.ui.dragStart) setHover({ x, y });
  }, []);

  const onMouseUp = useCallback(
    (x: number, y: number) => {
      const { doc: d, ui: u, editavel: canEdit } = stateRef.current;
      const start = u.dragStart;
      // Devolve o MESMO `ui` quando não havia arrasto: um objeto novo aqui mudaria o
      // MetaContext e re-renderizaria Canvas e Inspector à toa a cada clique.
      setUi((prev) => (prev.dragStart ? { ...prev, dragStart: null } : prev));
      setHover(null);
      if (!start || !canEdit) return;

      // Clique simples (mousedown e mouseup na MESMA célula): posiciona o cursor do
      // teclado. Antes esse gesto era descartado como "arrasto inválido" e não havia
      // como corrigir uma letra depois de colocada.
      if (start.x === x && start.y === y) {
        setUi((prev) => ({ ...prev, cursor: { x, y } }));
        if (gridRef.current) gridRef.current.focus();
        return;
      }

      const line: Line = { x1: start.x, y1: start.y, x2: x, y2: y };
      const cells = cellsOf(line);
      if (cells.length < 2) return; // reta inválida (nem H, nem V, nem 45°)

      // Reta que JÁ é de uma palavra: arrastar de novo por cima gravaria uma segunda
      // WordEntry com a mesma solução — duplicata que o `validate` só acusa como
      // `warning` (o Salvar não bloqueia) e que quebra a marcação de dicas no jogo do
      // jogador. Aqui o gesto vale como "selecionar aquela palavra".
      const existing = d.words.find((w) => isSameLine(w, line));
      if (existing) {
        setUi((prev) => ({ ...prev, selectedRef: existing.ref }));
        // Sem a mensagem o gesto é MUDO: o único sinal seria o realce da seleção, que
        // some no primeiro passeio do mouse pela lista do Inspector — e aí o arrasto
        // "não fez nada" para o autor.
        message.info(`Esta reta já é da palavra ${existing.solution}.`);
        return;
      }

      // NUNCA gravar palavra sem ato explícito do autor. Mesmo com a reta 100%
      // preenchida — o caso normal depois de "Preencher vazias" — o papel das letras
      // que já estão na grade é PRÉ-PREENCHER o campo; quem confirma é o autor.
      //
      // Só a reta CHEIA pré-preenche: num "A _ _ T _" o `join('')` perderia a posição
      // das vazias e o campo abriria com "AT" sob o título "Palavra de 5 letras" —
      // um valor que o próprio modal recusa por tamanho. Reta com buraco abre vazia.
      const gridLetters = cells.map((c) => d.letters[cellIndex(c.x, c.y, d.width)]);
      const lineIsFull = gridLetters.every((l) => l !== '');
      setPending({ ...line, length: cells.length });
      setTyped(lineIsFull ? gridLetters.join('') : '');
      setPendingError(null);
    },
    [setUi],
  );

  /** Move o cursor, grudando nas bordas. Sem cursor, a primeira seta o cria em (1,1). */
  const moveCursor = useCallback(
    (dx: number, dy: number) => {
      const { doc: d } = stateRef.current;
      setUi((prev) => {
        if (!prev.cursor) return { ...prev, cursor: { x: 1, y: 1 } };
        return {
          ...prev,
          cursor: {
            x: Math.min(Math.max(prev.cursor.x + dx, 1), d.width),
            y: Math.min(Math.max(prev.cursor.y + dy, 1), d.height),
          },
        };
      });
    },
    [setUi],
  );

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (!stateRef.current.editavel) return; // somente leitura: nem cursor, nem letra
      // Não engolir atalho do navegador/SO (Ctrl+Z, Cmd+C…).
      if (e.ctrlKey || e.metaKey || e.altKey) return;

      switch (e.key) {
        case 'ArrowLeft':
          e.preventDefault();
          return moveCursor(-1, 0);
        case 'ArrowRight':
          e.preventDefault();
          return moveCursor(1, 0);
        case 'ArrowUp':
          e.preventDefault();
          return moveCursor(0, -1);
        case 'ArrowDown':
          e.preventDefault();
          return moveCursor(0, 1);
        default:
          break;
      }

      // A grade CONSOME as teclas de edição — toda tecla de caractere (`key` de um
      // caractere só) e as de apagar —, mesmo quando não há nada para escrever: com a
      // grade focada, um espaço que passasse rolaria a página. Tab e Enter ficam de
      // fora de propósito (`key` mais longo): Tab precisa continuar trocando o foco.
      const erasing = e.key === 'Backspace' || e.key === 'Delete';
      if (!erasing && e.key.length !== 1) return;
      e.preventDefault();

      const cursor = stateRef.current.ui.cursor;
      if (!cursor) return;

      // A acentuação segue a MESMA regra do resto do módulo: `normalizeSolution`
      // (ç → C, á → A). Dígito, espaço e pontuação viram string vazia: consumidos,
      // mas não escrevem nada.
      const letter = erasing ? '' : normalizeSolution(e.key);
      if (!erasing && letter.length !== 1) return;

      // Escrever por cima de uma célula de palavra colocada continua PERMITIDO (dado
      // velho chega com grade e palavra já discordando, e o conserto é pela grade),
      // mas não pode ser silencioso: o diagnóstico que acusa a divergência fica no pé
      // da página, longe da tecla. `key` fixo para o aviso se substituir em vez de
      // empilhar um toast por letra digitada.
      //
      // A célula pode ser de DUAS palavras (cruzamento) — por isso `owners` é uma
      // lista, não uma só: um `find` aqui nomearia a palavra errada e, pior, ficaria
      // calado quando a letra digitada satisfaz a primeira mas quebra a segunda. O
      // aviso só se cala quando a letra bate com TODAS as que passam pela célula.
      const owners = wordsAtCell(stateRef.current.doc, cursor.x, cursor.y);
      const broken = owners.filter((o) => letter !== o.expected);
      if (broken.length > 0) {
        const solutions = joinSolutions(broken.map((o) => o.solution));
        const plural = broken.length > 1;
        message.warning({
          key: 'ws-word-desync',
          content: plural
            ? `Esta célula é das palavras ${solutions}: a grade vai discordar delas. Corrija a letra ou remova as palavras.`
            : `Esta célula é da palavra ${solutions}: a grade vai discordar dela. Corrija a letra ou remova a palavra.`,
        });
      }

      dispatch({ type: 'TYPE_LETTER', x: cursor.x, y: cursor.y, letter });
    },
    [dispatch, moveCursor],
  );

  const onMouseLeave = useCallback(() => {
    if (stateRef.current.ui.dragStart) setUi((prev) => ({ ...prev, dragStart: null }));
    setHover(null);
  }, [setUi]);

  const closePending = useCallback(() => setPending(null), []);

  const confirmPending = useCallback(() => {
    if (!pending) return;
    const solution = normalizeSolution(typed);
    // Tamanho diferente da reta: o PLACE_WORD seria descartado pelo reducer em
    // silêncio. Avisa no próprio modal e NÃO fecha — fechar como se tivesse dado
    // certo é o pior dos mundos, o autor só descobriria conferindo a lista.
    if (solution.length !== pending.length) {
      setPendingError(
        `A palavra precisa ter exatamente ${pending.length} letras (A–Z, sem acento). ` +
          `"${solution}" tem ${solution.length}.`,
      );
      return;
    }
    // O ref sai do `ui` corrente (não do capturado no arrasto): "n{N}" é a marca de
    // palavra criada no cliente e não pode colidir com os "w{id}" que vêm do servidor.
    const ref = newRef(stateRef.current.ui);
    dispatch({
      type: 'PLACE_WORD',
      ref,
      solution,
      x1: pending.x1,
      y1: pending.y1,
      x2: pending.x2,
      y2: pending.y2,
    });
    setUi((prev) => ({ ...prev, nextRef: prev.nextRef + 1, selectedRef: ref }));
    setPending(null);
  }, [pending, typed, dispatch, setUi]);

  return (
    <>
      <div
        ref={gridRef}
        role="grid"
        aria-label="Grade do caça-palavras"
        // Focável para receber teclado; o cursor é anunciado por activedescendant
        // (o foco do DOM fica na grade, não nas 225 células).
        tabIndex={0}
        aria-activedescendant={ui.cursor ? `ws-cell-${ui.cursor.x}-${ui.cursor.y}` : undefined}
        onKeyDown={onKeyDown}
        onMouseLeave={onMouseLeave}
        style={{ display: 'inline-block', padding: 8 }}
      >
        {Array.from({ length: doc.height }, (_, row) => (
          <div key={row} role="row" style={{ display: 'flex' }}>
            {Array.from({ length: doc.width }, (_, col) => {
              const x = col + 1;
              const y = row + 1;
              const key = `${x},${y}`;
              return (
                <Cell
                  key={key}
                  x={x}
                  y={y}
                  letter={doc.letters[cellIndex(x, y, doc.width)]}
                  inWord={wordCells.has(key)}
                  dragging={dragCells.has(key)}
                  selected={selectedCells.has(key)}
                  isCursor={ui.cursor != null && ui.cursor.x === x && ui.cursor.y === y}
                  onMouseDown={onMouseDown}
                  onMouseEnter={onMouseEnter}
                  onMouseUp={onMouseUp}
                />
              );
            })}
          </div>
        ))}
      </div>

      <Modal
        visible={pending !== null}
        title={pending ? `Palavra de ${pending.length} letras` : ''}
        okText="Colocar"
        cancelText="Cancelar"
        onOk={confirmPending}
        onCancel={closePending}
        destroyOnClose
      >
        <Input
          autoFocus
          placeholder="Digite a palavra"
          aria-label="Palavra"
          status={pendingError ? 'error' : undefined}
          value={typed}
          onChange={(e) => {
            setTyped(e.target.value);
            setPendingError(null);
          }}
          onPressEnter={confirmPending}
        />
        {pendingError && (
          <Typography.Text type="danger" style={{ display: 'block', marginTop: 8 }}>
            {pendingError}
          </Typography.Text>
        )}
      </Modal>
    </>
  );
};
